"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_BRAND_COMPLIANCE_SETTINGS = exports.AI_LABEL_BOX_PERCENT_SCHEMA = exports.AI_LABEL_WIDTH_FRACTION = exports.AI_LABEL_MIN_INSET = exports.BRAND_COMPLIANCE_SETTINGS_SCHEMA = exports.AI_LABEL_CONFIG_SCHEMA = exports.AI_LABEL_RESOLVED_TREATMENTS = exports.AI_LABEL_POSITIONS = exports.AI_LABEL_TREATMENTS = exports.AI_LABEL_VARIANTS = void 0;
exports.resolveAiLabelInsets = resolveAiLabelInsets;
exports.resolvesTreatment = resolvesTreatment;
exports.resolveCornerLabelBox = resolveCornerLabelBox;
exports.resolveAiLabelBoxPixels = resolveAiLabelBoxPixels;
exports.toWritableBrandComplianceSettings = toWritableBrandComplianceSettings;
exports.toAiLabelConfig = toAiLabelConfig;
exports.appliesToMedia = appliesToMedia;
const zod_1 = require("zod");
/**
 * Which label mark is stamped. `AI` is the compact EU circle mark; `AI_GENERATED` is the full EU
 * disclosure pill; `CUSTOM` is the brand's own uploaded asset, referenced from its compliance settings.
 *
 * `CUSTOM` has no bundled asset and no light/dark pair, so two things that hold for the other two
 * variants do NOT hold for it: `aiLabelTreatment` is inert (see `resolvesTreatment`), and selecting it
 * without an uploaded mark is invalid rather than merely unstyled.
 */
exports.AI_LABEL_VARIANTS = ['AI', 'AI_GENERATED', 'CUSTOM'];
/**
 * Which treatment of the mark is used. `AUTO` defers the choice to the compositor, which measures the
 * backdrop luminance under the label and picks whichever of light/dark actually contrasts.
 */
exports.AI_LABEL_TREATMENTS = ['LIGHT', 'DARK', 'AUTO'];
/** Which corner of the creative the mark is anchored to. */
exports.AI_LABEL_POSITIONS = ['TOP_LEFT', 'TOP_RIGHT', 'BOTTOM_LEFT', 'BOTTOM_RIGHT'];
/** The two treatments an asset actually exists for — what `AUTO` resolves to. */
exports.AI_LABEL_RESOLVED_TREATMENTS = ['LIGHT', 'DARK'];
exports.AI_LABEL_CONFIG_SCHEMA = zod_1.z
    .object({
    variant: zod_1.z.enum(exports.AI_LABEL_VARIANTS),
    treatment: zod_1.z.enum(exports.AI_LABEL_TREATMENTS),
    position: zod_1.z.enum(exports.AI_LABEL_POSITIONS),
})
    .strict();
exports.BRAND_COMPLIANCE_SETTINGS_SCHEMA = zod_1.z
    .object({
    aiLabelEnabled: zod_1.z.boolean(),
    /** Whether image placements are labelled ("Static images" in the compliance tab). */
    aiLabelApplyToStatic: zod_1.z.boolean(),
    /** Whether video placements are labelled ("Video & reels" in the compliance tab). */
    aiLabelApplyToVideo: zod_1.z.boolean(),
    aiLabelVariant: zod_1.z.enum(exports.AI_LABEL_VARIANTS),
    aiLabelTreatment: zod_1.z.enum(exports.AI_LABEL_TREATMENTS),
    aiLabelPosition: zod_1.z.enum(exports.AI_LABEL_POSITIONS),
    /**
     * The brand's own mark, by file UUID — never an internal id, which this shape is served straight
     * out of without a transformer to strip one.
     *
     * OPTIONAL on purpose, in a schema that is `.strict()` and a PUT that replaces the record whole.
     * A required field would 400 every client that has not shipped the new key yet. The three states
     * are distinct and all meaningful: omitted means "leave whatever is stored alone" (so an older
     * client saving settings cannot silently wipe a brand's uploaded mark), an explicit `null`
     * clears it, and a uuid sets it.
     */
    aiLabelFileUuid: zod_1.z.string().uuid().nullable().optional(),
})
    .strict()
    .superRefine((settings, ctx) => {
    // Only the unambiguous case belongs here: the schema cannot see what is already stored, so
    // "CUSTOM with the field omitted" may be perfectly valid against an existing mark. The service
    // owns the authoritative check against persisted state.
    if (settings.aiLabelVariant === 'CUSTOM' && settings.aiLabelFileUuid === null) {
        ctx.addIssue({
            code: 'custom',
            path: ['aiLabelFileUuid'],
            message: 'A custom label variant requires an uploaded mark.',
        });
    }
});
/**
 * Minimum inset of the mark from any edge, as a fraction. Also the inset used on every edge when a
 * placement has no safe zone — the two are unified so there is a single "how far from the edge" number.
 */
exports.AI_LABEL_MIN_INSET = 0.1;
/**
 * Per-edge inset of the mark, as a fraction of the frame dimension on that edge, resolved from a
 * placement's `safeZone`. The safe zone is where platform chrome sits (percentages 0–100 of the frame);
 * the mark is inset to clear it. Each edge is floored at `AI_LABEL_MIN_INSET`, so a declared `0` (no
 * chrome on that edge) still keeps the mark off the pixel edge, and a placement with no safe zone gets
 * `AI_LABEL_MIN_INSET` on every edge. There is deliberately NO upper bound: a deep safe zone (e.g. a 35%
 * bottom) is honoured in full, so the mark clears the platform's caption/CTA stack.
 *
 * The four numbers describe a box, not a point: only the two edges the chosen corner touches bind.
 * Shared so the backend compositor and the frontend preview overlay position the mark identically, and
 * the single home of the percentage→fraction conversion — the one surface where a units mistake would
 * silently clamp the mark into a corner rather than throw.
 */
function resolveAiLabelInsets(safeZone) {
    const edge = (percent) => Math.max((percent ?? 0) / 100, exports.AI_LABEL_MIN_INSET);
    return {
        top: edge(safeZone?.top),
        right: edge(safeZone?.right),
        bottom: edge(safeZone?.bottom),
        left: edge(safeZone?.left),
    };
}
/**
 * Width of the mark's visible ink, as a fraction of the creative's **shorter** edge. Keyed to the
 * shorter edge so a mark reads at the same size on a 1200x628 landscape and a 1080x1920 vertical.
 *
 * Per variant because the two marks have very different aspect ratios (the circle is 1:1, the pill
 * ~5.2:1): one shared fraction would make the pill's text illegible or the circle enormous. Shared
 * for the same compositor/preview parity reason as `resolveAiLabelInsets`.
 */
exports.AI_LABEL_WIDTH_FRACTION = {
    AI: 0.1,
    AI_GENERATED: 0.22,
    // A brand's own mark has no hand-tuned number available, so it borrows the compact mark's. This
    // single value governs the rendered size in BOTH the compositor and the frontend preview; a brand
    // that wants a different size positions the mark explicitly, which sets its width directly.
    CUSTOM: 0.1,
};
/**
 * Whether the `treatment` setting means anything for this variant.
 *
 * The bundled marks ship as a light/dark pair, so a treatment picks between them and `AUTO` measures the
 * backdrop to decide. A brand's own mark is ONE asset: there is nothing to pick, and measuring a backdrop
 * to choose between one option is wasted work on an interactive path. Callers use this rather than
 * testing `variant === 'CUSTOM'` inline, so the rule has one home.
 */
function resolvesTreatment(variant) {
    return variant !== 'CUSTOM';
}
/**
 * Bounds a box that arrives from OUTSIDE (a user edit, and later a model). Validation exists so an
 * out-of-range value is REJECTED rather than clamped: the clamp in `resolveAiLabelBoxPixels` would turn a
 * unit error into a believable box, which is the failure mode that stays hidden.
 *
 * Deliberately not applied to `resolveCornerLabelBox`'s own output — on a canvas small enough that the
 * insets overlap, the corner rule legitimately produces an off-canvas percentage that the converter's
 * clamp then resolves, which is the pre-existing behaviour.
 */
exports.AI_LABEL_BOX_PERCENT_SCHEMA = zod_1.z
    .object({
    xPercent: zod_1.z.number().min(0).max(100),
    yPercent: zod_1.z.number().min(0).max(100),
    widthPercent: zod_1.z.number().min(0).max(100),
})
    .strict();
/**
 * The mark's pixel size for a given ideal width, shrunk proportionally if it would not fit.
 *
 * Shared by the corner producer and the percent→pixel converter so there is exactly ONE sizing rule: the
 * corner producer needs the fitted size to place a bottom/right anchor, and the converter needs it to
 * emit the final box. Two copies would drift the moment either is tuned.
 */
function fitMarkPixels(options) {
    const { idealWidth, inkAspectRatio, creativeWidth, creativeHeight } = options;
    const idealHeight = idealWidth / inkAspectRatio;
    // A very tall mark on a very small creative can exceed the canvas even at its nominal fraction;
    // shrink it proportionally rather than emit a box that runs off the edge.
    const fit = Math.min(1, creativeWidth / idealWidth, creativeHeight / idealHeight);
    return {
        width: Math.max(1, Math.floor(idealWidth * fit)),
        height: Math.max(1, Math.floor(idealHeight * fit)),
    };
}
/**
 * The DEFAULT geometry — the corner-and-safe-zone rule, expressed as the same box a stored override uses.
 *
 * Expressing it this way is what makes "exactly one place where percentages become pixels" true rather
 * than aspirational: every source of a label position (this rule, a user's edit, and later a model's
 * answer) produces an `IAiLabelBoxPercent`, and `resolveAiLabelBoxPixels` is the only thing that converts.
 *
 * `widthPercent` carries the mark's **ideal** width (pre-fit), so the converter reproduces the identical
 * shrink. The anchor is computed against the FITTED size, because a bottom- or right-anchored mark's
 * top-left depends on how big it actually ended up.
 */
function resolveCornerLabelBox(options) {
    const { creativeWidth, creativeHeight, variant, position, inkAspectRatio, safeZone } = options;
    const idealWidth = exports.AI_LABEL_WIDTH_FRACTION[variant] * Math.min(creativeWidth, creativeHeight);
    const { width, height } = fitMarkPixels({ idealWidth, inkAspectRatio, creativeWidth, creativeHeight });
    const insets = resolveAiLabelInsets(safeZone);
    const anchoredRight = position === 'TOP_RIGHT' || position === 'BOTTOM_RIGHT';
    const anchoredBottom = position === 'BOTTOM_LEFT' || position === 'BOTTOM_RIGHT';
    const left = anchoredRight ? creativeWidth - insets.right * creativeWidth - width : insets.left * creativeWidth;
    const top = anchoredBottom ? creativeHeight - insets.bottom * creativeHeight - height : insets.top * creativeHeight;
    return {
        xPercent: (left / creativeWidth) * 100,
        yPercent: (top / creativeHeight) * 100,
        widthPercent: (idealWidth / creativeWidth) * 100,
    };
}
/**
 * The ONLY place percentages become pixels.
 *
 * The terminal clamp is not validation — it is what makes the two compositors agree. Sharp REJECTS an
 * overlay larger than its canvas while ffmpeg happily draws off-frame, so without a single shared
 * constraint the same box would hard-fail a static and silently clip a video. Reject an out-of-range box
 * at its entry point (`AI_LABEL_BOX_PERCENT_SCHEMA`); by the time it reaches here, clamping is the
 * correct and last-resort behaviour for a degenerate canvas.
 */
function resolveAiLabelBoxPixels(options) {
    const { box, creativeWidth, creativeHeight, inkAspectRatio } = options;
    const idealWidth = (box.widthPercent / 100) * creativeWidth;
    const { width, height } = fitMarkPixels({ idealWidth, inkAspectRatio, creativeWidth, creativeHeight });
    const left = Math.round((box.xPercent / 100) * creativeWidth);
    const top = Math.round((box.yPercent / 100) * creativeHeight);
    return {
        left: clampToRange(left, 0, Math.max(0, creativeWidth - width)),
        top: clampToRange(top, 0, Math.max(0, creativeHeight - height)),
        width,
        height,
    };
}
function clampToRange(value, min, max) {
    return Math.min(Math.max(value, min), max);
}
/**
 * Every key a caller may actually WRITE.
 *
 * Typed as a total `Record<keyof IBrandComplianceSettings, true>` on purpose: the settings READ shape is
 * a superset (it carries the resolved mark file and its ink ratio, neither of which is settable), and
 * the settings form holds whatever it was given and saves it back wholesale. Without a projection those
 * read-only fields ride along into a `.strict()` PUT and every save fails with "Unrecognized keys" —
 * including saves that never touched the mark.
 *
 * The `Record` is the tripwire: a field added to the schema but not listed here is a compile error, not
 * a runtime surprise on someone's settings page.
 */
const WRITABLE_BRAND_COMPLIANCE_SETTINGS_KEYS = {
    aiLabelEnabled: true,
    aiLabelApplyToStatic: true,
    aiLabelApplyToVideo: true,
    aiLabelVariant: true,
    aiLabelTreatment: true,
    aiLabelPosition: true,
    aiLabelFileUuid: true,
};
/**
 * Narrow anything settings-shaped down to the fields the update endpoint accepts.
 *
 * Applied at the mutation boundary rather than asked of each caller, so a surface that renders the read
 * shape cannot accidentally save it.
 */
function toWritableBrandComplianceSettings(settings) {
    const writable = {};
    for (const key of Object.keys(WRITABLE_BRAND_COMPLIANCE_SETTINGS_KEYS)) {
        // `undefined` is meaningful for the optional mark reference — it means "leave the stored mark
        // alone" — so an absent key must stay absent rather than becoming an explicit undefined.
        if (key in settings)
            writable[key] = settings[key];
    }
    return writable;
}
/**
 * Settings a brand has when it has never configured compliance: labelling off. The mark defaults mirror
 * the ticket's stated fallback — "the default one (white label, black font 'AI')", i.e. the compact
 * circle in its light treatment.
 */
exports.DEFAULT_BRAND_COMPLIANCE_SETTINGS = {
    aiLabelEnabled: false,
    aiLabelApplyToStatic: true,
    aiLabelApplyToVideo: true,
    aiLabelVariant: 'AI',
    aiLabelTreatment: 'LIGHT',
    aiLabelPosition: 'BOTTOM_RIGHT',
    aiLabelFileUuid: null,
};
/** The label configuration implied by a brand's stored settings, ignoring whether labelling is on. */
function toAiLabelConfig(settings) {
    return {
        variant: settings.aiLabelVariant,
        treatment: settings.aiLabelTreatment,
        position: settings.aiLabelPosition,
    };
}
/** Whether a brand labels the given media kind at all (the per-media "Apply to" toggles). */
function appliesToMedia(settings, media) {
    return media === 'IMAGE' ? settings.aiLabelApplyToStatic : settings.aiLabelApplyToVideo;
}
